import type { ResourceRole, RoomMode, StoredVariant } from '@os/shared';

/**
 * The web app's only door to the backend. `createSupabaseApi` talks to Supabase (RLS and RPCs enforce every
 * rule); `createFakeApi` mirrors those rules in memory for component tests.
 */

import type { FigmaNode } from '@os/doc';
import type { UnfurlResult } from '../features/links/services';
import type { CommentAnchor, CommentMessage, CommentNotification, CommentThread, Mentionable } from './comment-types';
import type { BoardVersion } from './version-types';
import type {
  AccessEntry,
  AccessFlags,
  AuditEntry,
  BoardPerson,
  EmailStatus,
  GrantRole,
  GrantScope,
  Invitation,
  InvitationPeek,
  InviteInput,
  InviteScope,
  LinkExpiry,
  SharedItem,
  ShareLink,
} from './sharing-types';

export type ApiErrorCode =
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'invalid'
  | 'too_large'
  | 'quota'
  | 'network'
  | 'expired'
  | 'account_required'
  | 'email_mismatch'
  | 'already_member'
  | 'limit'
  /** `/invite` (P17): the invitation is unknown, revoked or already used / past its expiry; a Worker answer, unlike `expired`. */
  | 'invitation_invalid'
  | 'invitation_expired'
  /** Signing up through an invitation: this address already has an account (the person signs in instead). */
  | 'account_exists'
  /** Changing the address (P17): wrong current password, address in use, same address, captcha refused. */
  | 'invalid_credentials'
  | 'email_taken'
  | 'unchanged'
  | 'captcha_failed'
  /** Supabase refuses writes (restricted project, 402, or read-only database, P12). */
  | 'read_only';

export class ApiError extends Error {
  constructor(
    readonly code: ApiErrorCode,
    message: string = code,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** What the Worker's Figma proxy refused (P11b); `retryAfter` in seconds for `figma_rate`. */
export type FigmaErrorCode = 'figma_token' | 'figma_private' | 'figma_not_found' | 'figma_rate' | 'figma_too_large' | 'figma_upstream' | 'forbidden' | 'rate' | 'invalid' | 'network';
export class FigmaImportError extends Error {
  constructor(
    readonly code: FigmaErrorCode,
    readonly retryAfter: number | null = null,
  ) {
    super(code);
    this.name = 'FigmaImportError';
  }
}

export type OrgRole = 'member' | 'admin' | 'owner';
export type Locale = 'fr' | 'en';
export type LifecycleKind = 'board' | 'project';
export type LifecycleAction = 'archive' | 'unarchive' | 'trash' | 'restore' | 'purge';

export type AuthUser = { id: string; email: string | null; isAnonymous: boolean };
export type Org = { id: string; name: string; slug: string; role: OrgRole };

export type Project = {
  id: string;
  orgId: string;
  name: string;
  clientName: string | null;
  createdBy: string | null;
  archivedAt: string | null;
  deletedAt: string | null;
  updatedAt: string;
  /** Manual order on the dashboard (P16-4): a fractional key, compared byte by byte; 'a0' until reordered. */
  position: string;
  /** P17: the organization's system project « Sans projet », shown as a dashboard section, never as a project. */
  isInbox: boolean;
};

export type Folder = { id: string; orgId: string; projectId: string; parentId: string | null; name: string; position: string };

/** Fixed template categories (plan P10 Ruling 5): the gallery filter, checked by `board_save_template`. */
export const TEMPLATE_CATEGORIES = ['moodboard', 'references', 'presentation', 'other'] as const;
export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number];

export type Board = {
  id: string;
  orgId: string;
  projectId: string;
  folderId: string | null;
  title: string;
  createdBy: string | null;
  archivedAt: string | null;
  deletedAt: string | null;
  updatedAt: string;
  lastEditedAt: string | null;
  /** Viewers see the comments (spec §9.2, per board, default true). */
  viewerComments: boolean;
  /** A custom template (spec §8): set by `saveAsTemplate` only, left out of project lists. */
  isTemplate: boolean;
  templateCategory: TemplateCategory | null;
};

/** What `board_access` returns: a board-only external or a guest sees the project's name, never its row. */
export type ProjectSummary = Pick<Project, 'id' | 'name' | 'archivedAt' | 'deletedAt'>;
export type BoardAccess = {
  board: Board;
  project: ProjectSummary;
  role: ResourceRole;
  flags: AccessFlags;
  isMember: boolean;
  /** Access through secret links only: such guests never resolve threads (P7 Ruling 3). */
  linkOnly: boolean;
};
export type Recent = { board: Board; viewedAt: string };
export type SearchHit = { kind: 'project' | 'board' | 'board_text'; id: string; title: string; projectId: string; projectName: string };
/** `avatarKey`: the R2 key of the person's photo (P5g), set through the Worker's /api/me/avatar, never here. */
export type Profile = { id: string; displayName: string; avatarKey: string | null; locale: Locale; color: string | null; commentDigest: boolean };
export type Member = { userId: string; role: OrgRole; displayName: string };

/** Outside the trash, templates are listed only with `templates: true`; the trash shows both. */
export type BoardQuery = { orgId: string; projectId?: string; trashed?: boolean; templates?: boolean };

/** Sent with every variant of an upload (`X-Asset-Meta`). `sha256`: hex of the original file's bytes. */
export type AssetUploadMeta = { sha256: string; mime: string; width: number; height: number; thumbhash: string; expected: StoredVariant[] };
/** An asset as `asset_urls` returns it: R2 keys of its uploaded variants (join with `mediaUrl`). */
export type RemoteAsset = {
  assetId: string;
  mime: string;
  width: number;
  height: number;
  thumbhash: string;
  status: 'uploading' | 'ready' | 'orphaned' | 'deleting';
  keys: Partial<Record<StoredVariant, string>>;
};
export type VariantUpload = { boardId: string; assetId: string; variant: StoredVariant; blob: Blob; meta: AssetUploadMeta };
/** Board thumbnails are 640 × 400 px (spec §8). */
export const THUMB_SIZE = { w: 640, h: 400 } as const;

/** The quotas of the day (P12, spec §18.4), as `GET /api/admin/quotas` returns them; days are UTC days. */
export type Quotas = {
  day: string;
  doRequests: number;
  /** Share of the day's Durable Object requests used, can go past 1. */
  doRatio: number;
  mode: RoomMode;
  storage: { used: number; limit: number; ratio: number };
  /** The Durable Object duration has no counter (P12 Ruling 3). */
  durationMeasured: false;
  resetAt: string;
};

export type LightenDays = 7 | 30 | 90;
export type LightenPreview = { versionCount: number; versionBytes: number; orphanCount: number; orphanBytes: number };

/**
 * One board in the storage report (P16-4): an image shared by several boards counts in each of them
 * (`sharedImageCount` of the row's images are shared), so rows do not add up to the organization's total.
 */
export type StorageRow = {
  boardId: string;
  title: string;
  projectId: string;
  projectName: string;
  ownerId: string | null;
  ownerName: string | null;
  docBytes: number;
  imageBytes: number;
  imageCount: number;
  sharedImageCount: number;
  versionBytes: number;
  versionCount: number;
  objectCount: number;
  lastEditedAt: string | null;
  archivedAt: string | null;
  deletedAt: string | null;
};

export interface AuthApi {
  getUser(): Promise<AuthUser | null>;
  onChange(listener: (user: AuthUser | null) => void): () => void;
  /** `captchaToken`: Turnstile, required by Supabase Auth once its captcha is enabled (P6 Ruling 5). */
  signInWithPassword(email: string, password: string, captchaToken?: string): Promise<void>;
  signInWithOtp(email: string, redirectTo: string, captchaToken?: string): Promise<void>;
  signInWithGoogle(redirectTo: string): Promise<void>;
  exchangeCode(code: string): Promise<void>;
  resetPassword(email: string, redirectTo: string, captchaToken?: string): Promise<void>;
  /** A guest without an account (spec §9.1); its rights come from share links only. */
  signInAnonymously(captchaToken?: string): Promise<void>;
  updatePassword(password: string): Promise<void>;
  signOut(): Promise<void>;
}

export interface Api {
  auth: AuthApi;
  /** False in local mode (no Supabase): sharing is hidden. `boardCopy`: import into a new board, duplication (P9). */
  /** `avatars`: profile photos go through the Worker (P5g), so not in local mode. */
  /** `figma`: the Worker's Figma import proxy is there (P11b). */
  readonly capabilities: { sharing: boolean; comments: boolean; versions: boolean; boardCopy: boolean; avatars: boolean; figma: boolean };
  myOrgs(): Promise<Org[]>;

  /** The system project « Sans projet » (`isInbox`) is never in this list: see `orgInbox`. */
  listProjects(orgId: string, opts?: { trashed?: boolean }): Promise<Project[]>;
  /** Id of the organization's « Sans projet » project (P17), created when missing; members only (`forbidden` otherwise). */
  orgInbox(orgId: string): Promise<string>;
  getProject(id: string): Promise<Project>;
  createProject(input: { orgId: string; name: string; clientName?: string | null }): Promise<Project>;
  updateProject(id: string, patch: { name?: string; clientName?: string | null }): Promise<void>;
  /** Writes new manual positions in one call (P16-4), archived projects included; returns how many changed. */
  reorderProjects(orgId: string, positions: readonly { id: string; position: string }[]): Promise<number>;

  listFolders(projectId: string): Promise<Folder[]>;
  createFolder(input: { orgId: string; projectId: string; name: string }): Promise<Folder>;
  renameFolder(id: string, name: string): Promise<void>;
  /** Moves a folder in its project's order (P16-4). */
  setFolderPosition(id: string, position: string): Promise<void>;
  deleteFolder(id: string): Promise<void>;

  listBoards(query: BoardQuery): Promise<Board[]>;
  /** `not_found` for unknown boards and for boards the caller cannot see (no difference on purpose). */
  getBoard(id: string): Promise<BoardAccess>;
  createBoard(input: { orgId: string; projectId: string; folderId?: string | null; title: string }): Promise<Board>;
  updateBoard(id: string, patch: { title?: string; projectId?: string; folderId?: string | null; viewerComments?: boolean }): Promise<void>;
  lifecycle(kind: LifecycleKind, action: LifecycleAction, id: string): Promise<void>;

  listRecents(limit?: number): Promise<Recent[]>;
  listFavorites(): Promise<Board[]>;
  setFavorite(boardId: string, on: boolean): Promise<void>;
  touchBoardView(boardId: string): Promise<void>;
  searchTitles(q: string): Promise<SearchHit[]>;

  getProfile(): Promise<Profile>;
  updateProfile(patch: Partial<Omit<Profile, 'id' | 'avatarKey'>>): Promise<void>;
  /** My photo (P5g): a 256 px WebP the Worker keeps under a key of mine, the previous one deleted; returns the key. */
  setAvatar(webp: Blob): Promise<string>;
  /** Back to my initials (the file is deleted). */
  removeAvatar(): Promise<void>;
  listMembers(orgId: string): Promise<Member[]>;
  addMember(orgId: string, email: string, role: Exclude<OrgRole, 'owner'>): Promise<void>;
  removeMember(orgId: string, userId: string): Promise<void>;

  /** A 60 s room ticket for /sync/:boardId (Worker, spec §7.4); `not_found` when the caller cannot see the board. */
  getRoomTicket(boardId: string): Promise<{ ticket: string; role: ResourceRole }>;

  /** Id of the organization's asset with this content, or null (spec §10.1 step 3); `forbidden` below editor. */
  checkAsset(boardId: string, sha256: string): Promise<string | null>;
  /** Uploaded variants of the given assets referenced by the board (viewer and up). */
  assetUrls(boardId: string, assetIds: string[]): Promise<RemoteAsset[]>;
  /** One variant to R2 through the Worker; `duplicateOf` when the organization already has this content. */
  putAssetVariant(upload: VariantUpload): Promise<{ status: 'uploading' | 'ready' } | { duplicateOf: string }>;
  putBoardThumbnail(boardId: string, blob: Blob): Promise<void>;
  /** Board id → R2 key of its 640 px thumbnail, for the boards the caller can see. */
  boardThumbnails(boardIds: string[]): Promise<Map<string, string>>;
  orgStorage(orgId: string): Promise<{ usedBytes: number; quotaBytes: number }>;
  /** The quotas of the day (P12), owner and admins only. */
  getQuotas(orgId: string): Promise<Quotas>;
  /** Every board of the organization with what it stores (P16-4, members only). */
  storageReport(orgId: string): Promise<StorageRow[]>;
  /** What "Lighten" would free (P16-4); members may look. `days`: 7, 30 or 90. */
  lightenPreview(orgId: string, days: LightenDays, versions: boolean): Promise<LightenPreview>;
  /** Lightens (admins and the owner): old automatic versions now, unreferenced images after the GC's grace. */
  lighten(orgId: string, input: { days: LightenDays; versions: boolean; images: boolean }): Promise<{ versionCount: number; orphanCount: number }>;

  /** Link preview or embed check by the Worker (spec §6); `network` when it cannot fetch the page. */
  unfurl(url: string): Promise<UnfurlResult>;
  /** A preview image fetched once by the Worker (JPEG/PNG/WebP/GIF ≤ 5 MiB), to be stored as a board asset. */
  unfurlImage(url: string): Promise<Blob>;

  /**
   * A Figma file read by the Worker for an editor of `boardId` (P11b): its name, tree and image download URLs. The
   * token is sent once, in the body, and never kept. Throws `FigmaImportError`.
   */
  figmaFile(input: { boardId: string; fileKey: string; token: string }, signal?: AbortSignal): Promise<{ name: string; document: FigmaNode; images: Record<string, string> }>;
  /** An image fill relayed by the Worker from Figma's hosts; null when it cannot be fetched. */
  figmaImage(boardId: string, url: string, signal?: AbortSignal): Promise<Blob | null>;

  // Sharing (spec §9.2-9.3) ----------------------------------------------------------------------------------
  /** The token is returned once: only its hash is stored, so the full URL cannot be shown again. */
  createShareLink(input: { boardId: string; role: GrantRole; expires: LinkExpiry } & AccessFlags): Promise<{ link: ShareLink; token: string }>;
  listShareLinks(boardId: string): Promise<ShareLink[]>;
  revokeShareLink(id: string): Promise<void>;
  /** `not_found` for unknown or revoked links, `expired`, or `account_required` for an editor link and a guest. */
  redeemShareLink(token: string): Promise<{ boardId: string; role: ResourceRole }>;
  listAccess(scope: GrantScope): Promise<AccessEntry[]>;
  setGrant(scope: GrantScope, userId: string, grant: { role: GrantRole; canExport: boolean; canDownload: boolean; expiresAt: string | null }): Promise<void>;
  removeGrant(scope: GrantScope, userId: string): Promise<void>;
  /** Through the Worker (Resend under a daily quota); the link is always returned for a manual copy. */
  invite(input: InviteInput): Promise<{ id: string; link: string; emailStatus: EmailStatus }>;
  listInvitations(scope: InviteScope): Promise<Invitation[]>;
  revokeInvitation(id: string): Promise<void>;
  /** `email_mismatch` when signed in with another address than the invited one. */
  acceptInvitation(token: string): Promise<InviteScope>;
  /** What an invitation is for, before any account exists (Worker, no credentials): `invitation_invalid` | `invitation_expired`. */
  peekInvitation(token: string): Promise<InvitationPeek>;
  /**
   * Creates the account of the invited address with the password the person chose (Worker, admin API: the only way in
   * now, P17); signing in and accepting follow. `account_exists` sends the person to sign in instead.
   */
  signupWithInvitation(token: string, input: { password: string; displayName: string; locale: Locale }): Promise<{ email: string }>;
  /** Changes my sign-in address after re-checking my password (Worker); the session is refreshed so the new one shows. */
  changeEmail(input: { email: string; password: string; captchaToken?: string }): Promise<{ email: string }>;
  sharedWithMe(): Promise<SharedItem[]>;
  /** Admins; newest first, `before` = the smallest id already shown. */
  auditLog(orgId: string, opts?: { before?: number; limit?: number }): Promise<AuditEntry[]>;

  // Comments (P7): read under RLS, written by RPCs.
  listThreads(boardId: string): Promise<CommentThread[]>;
  threadMessages(threadId: string): Promise<CommentMessage[]>;
  createThread(input: { boardId: string; anchor: CommentAnchor; body: string }): Promise<string>;
  replyComment(threadId: string, body: string): Promise<string>;
  editComment(id: string, body: string): Promise<void>;
  deleteComment(id: string): Promise<void>;
  setThreadResolved(id: string, resolved: boolean): Promise<void>;
  deleteThread(id: string): Promise<void>;
  /** People who read the board's comments (commenters and above may ask). */
  mentionables(boardId: string): Promise<Mentionable[]>;
  /** Names of objects' authors (P5d) among the board's current readers; ids not returned are former members. At most 200. */
  authors(boardId: string, userIds: string[]): Promise<Mentionable[]>;
  /** Who has access to the board, with their effective role (viewer and up; P16-3 members panel). */
  boardPeople(boardId: string): Promise<BoardPerson[]>;
  myNotifications(): Promise<CommentNotification[]>;
  /** All unread notifications, or one thread's; returns how many were marked. */
  markNotificationsRead(threadId?: string): Promise<number>;

  // Versions (P8): listed under RLS, written by the board's room through the Worker.
  /** Newest first, 200 at most (viewer and up). */
  listVersions(boardId: string): Promise<BoardVersion[]>;
  /** A manual named version (editor and up); `limit` past 100, `invalid` for an empty or too long label. */
  createVersion(boardId: string, label: string): Promise<{ id: string }>;
  /** The version file (gzip Yjs update) for the read-only preview. */
  fetchVersion(boardId: string, versionId: string): Promise<Uint8Array>;
  /** Restores the version on the live board (§9.2: not for link guests); `conflict` while another restore runs. */
  restoreVersion(boardId: string, versionId: string): Promise<void>;
  /** Manual versions only, with the restore right. */
  deleteVersion(versionId: string): Promise<void>;

  // Board copy (P9): an exported file deduplicates on import; boards are seeded and duplicated by their rooms.
  /** Content hash (hex SHA-256) of the board's assets and those of its versions, by asset id (viewer and up). */
  assetHashes(boardId: string, assetIds: string[]): Promise<Map<string, string>>;
  /** Fills an empty board from a version file (editor and up); `conflict` when it has objects, `too_large` past 64 MB. */
  seedBoard(boardId: string, bytes: Uint8Array): Promise<void>;
  /** A copy of the board in another project of the organization (members only); `not_found` otherwise. */
  duplicateBoard(boardId: string, input: { projectId: string; title: string }): Promise<{ boardId: string }>;

  // Templates (P10): custom templates are boards flagged by `board_save_template`.
  /** The organization's templates outside the trash, whose project is not trashed either, newest first. */
  listTemplates(orgId: string): Promise<Board[]>;
  /** A template copy of the board in its project (members only); `invalid` for a bad title or category. */
  saveAsTemplate(boardId: string, input: { title: string; category: TemplateCategory }): Promise<{ boardId: string }>;
}

export function canDelete(role: OrgRole, createdBy: string | null, userId: string): boolean {
  return role !== 'member' || createdBy === userId;
}

/** Fractional keys order byte by byte (the `C` collation), never by locale: 'Zz' sorts before 'a0'. */
export const byPosition = (a: { position: string }, b: { position: string }): number => (a.position < b.position ? -1 : a.position > b.position ? 1 : 0);
